import moment from "moment";
import AdminService from 'external-services/diary-admin-service/admin.service';
import { MAX_CHAIRS, MAX_CHAIRS_LEGACY } from "floorplan-v2/app/appRoot/constants";
import {
  CHAIR_EDGE,
  CHAIR_GAP,
  CHAIR_W,
} from 'floorplan-v2/app/components/Tables/Chair/types';
/**
 * Has dependency on floorplan-bundle.js
 */

const NS = 'TableHelpersService';

/**
 * Description:
 * Static helper functions and constants for tables (src/app/plan/model/table.js).
 * Gets loaded as part of 'floorplan' module.
 *
 * External dependencies:
 * - moment
 * - underscore
 */

export default function TableHelpersService(SnapConst, SnapService, PlanConst) {
  "ngInject";

  const THIS_SERVICE = {};

  THIS_SERVICE.Type = 'table';

  THIS_SERVICE.Sizes = {
    Body: {
      w: 80,
      h: 80
    },
    Chair: {
      w: 50,
      h: 16
    }
  };

  THIS_SERVICE.Statuses = {
    Free: 'free',
    Next: 'next',
    Current: 'current',
    Overdue: 'overdue'
  };

  THIS_SERVICE.Conflicts = {
    None: 'none',
    Warning: 'warning',
    Error: 'error'
  };

  //Return the chairs array if present and coherent with the capacity, null otherwise
  THIS_SERVICE.validateChairs = function (model) {
    if (model.chairs) {
      var total = 0;
      _.each(model.chairs, function (side) {
        if (side) {
          total += side;
        }
      });
      if (total === THIS_SERVICE.getCapacity(model.capacity)) {
        return model.chairs;
      }
    }
    return null;
  };

  THIS_SERVICE.getChairsDistribution = function (model) {
    if (model.chairs) {
      var chairs = [];
      _.times(4, function (n) {
        chairs.push(model.chairs[n] != null ? model.chairs[n] : 0);
      }, this);
      if (_.max(chairs) > 0) {
        return chairs;
      }
    }
    var result = [];
    const cap = THIS_SERVICE.getCapacity(model.capacity);
    switch (THIS_SERVICE.getShape(model.shape)) {
      case SnapConst.Shapes.Circle:
        result = _.times(cap, function () {
          return 1;
        });
        break;
      case SnapConst.Shapes.Rect:
        result = [0, 0, 0, 0];
        _.times(cap, function (n) {
          n++;
          if (n === 1) {
            result[0]++;
          } else if (n === 2) {
            result[2]++;
          } else {
            var m = n % 4;
            switch (m) {
              case 3:
                result[0]++;
                break;
              case 0:
                result[2]++;
                break;
              case 1:
                result[3]++;
                break;
              case 2:
                result[1]++;
                break;
            }
          }
        }, this);
        break;
      default:
        result = [0, 0, 0, 0];
        _.times(cap, function (n) {
          switch (n % 4) {
            case 0:
              result[0]++;
              break;
            case 1:
              result[2]++;
              break;
            case 2:
              result[1]++;
              break;
            case 3:
              result[3]++;
              break;
          }
        });
        break;
    }
    return result;
  };

  // Don't let plan draw tables bigger than 20 pax (NBI-353), but if enableNewFloorPlan is on then change MAX to 8 pax
  THIS_SERVICE.getCapacity = function (originalCapacity) {
    const venue = AdminService.getVenue();
    const maxChair = venue.enableNewFloorPlan ? MAX_CHAIRS : MAX_CHAIRS_LEGACY
    return originalCapacity >= maxChair ? maxChair : originalCapacity;
  };

  THIS_SERVICE.getSize = function (model, useCapacity) {
    var result = {
      w: 0,
      h: 0
    };
    var chairs = THIS_SERVICE.getChairsDistribution(model);
    const venue = AdminService.getVenue();
    const increment = CHAIR_W + CHAIR_GAP;
    switch (THIS_SERVICE.getShape(model.shape)) {
      case SnapConst.Shapes.Rect:
        if (venue.enableNewFloorPlan) {
          // New FloorPlan way
          result.w = (CHAIR_EDGE * 2) - CHAIR_GAP + Math.max(chairs[0], chairs[2], 1) * increment;
          result.h = (CHAIR_EDGE * 2) - CHAIR_GAP + Math.max(chairs[1], chairs[3], 1) * increment;
          break;
        }
        if (useCapacity) {
          const w = Math.max(chairs[0], chairs[2], 1);
          const h = Math.max(chairs[1], chairs[3], 1);
          result.w = w * THIS_SERVICE.Sizes.Body.w;
          result.h = h * THIS_SERVICE.Sizes.Body.w;
        } else {
          result.w = THIS_SERVICE.Sizes.Body.w * 2;
          result.h = THIS_SERVICE.Sizes.Body.w;
        }
        break;
      case SnapConst.Shapes.Square:
        if (venue.enableNewFloorPlan) {
          result.w = (CHAIR_EDGE * 2) - CHAIR_GAP + Math.max(chairs[0], chairs[2], 1) * increment;
          result.h = result.w;
          break;
        }
        if (useCapacity) {
          result.w = Math.max(chairs[0], chairs[1], chairs[2], chairs[3], 1) * THIS_SERVICE.Sizes.Body.w;
        } else {
          result.w = THIS_SERVICE.Sizes.Body.w;
        }
        result.h = result.w;
        break;
      case SnapConst.Shapes.Circle:
        if (useCapacity) {
          result.w = Math.max(70 * THIS_SERVICE.getCapacity(model.capacity) / Math.PI, THIS_SERVICE.Sizes.Body.w);
        } else {
          result.w = THIS_SERVICE.Sizes.Body.w;
        }
        result.h = result.w;
        break;
    }
    return result;
  };

  THIS_SERVICE.rootBBox = function (model) {
    var pos = model.position || {
      x: 0,
      y: 0
    };
    var size = THIS_SERVICE.getSize(model, true);
    var rot = model.rotation || 0;
    if (SnapConst.Shapes.Circle === THIS_SERVICE.getShape(model.shape)) {
      rot = 0;
    }
    return SnapService.getRectBox(size, pos, rot);
  };

  /*
  *  Iterate each blockout in the schedule
  *  Iterate the blockedSections and match the model.sectionId
  *  Iterate the blockedTables and match the model._id
  *  On matching blockouts add the entry to the result as with _id, name, start, end, source
  */
  THIS_SERVICE.getBlockouts = function (mode, model, schedule) {
    var result = [];
    if (mode === PlanConst.Mode.Bookings) {
      _.each(schedule.blockouts, function (blockout) {
        _.each(_.filter(blockout.blockedSections, function (bs) {
          return bs === model.sectionId;
        }), function (sectionId) {
          result.push({
            _id: blockout._id,
            name: blockout.name,
            start: moment(blockout.startDateTime),
            end: moment(blockout.endDateTime),
            source: sectionId
          });
        });
        _.each(_.filter(blockout.blockedTables, function (bt) {
          return bt._id === model._id;
        }), function (tableId) {
          result.push({
            _id: blockout._id,
            name: blockout.name,
            start: moment(blockout.startDateTime),
            end: moment(blockout.endDateTime),
            source: tableId._id
          });
        });
      });
    }
    return result;
  };

  THIS_SERVICE.getShape = function (shape) {
    return _.contains([SnapConst.Shapes.Circle, SnapConst.Shapes.Rect], shape) ? shape : SnapConst.Shapes.Square;
  };

  //Return the model.position if is valid, return null otherwise
  THIS_SERVICE.verifyPosition = function (model) {
    if (model.position) {
      var box = THIS_SERVICE.rootBBox(model);
      if (box.x >= 0 && box.y > 0) {
        return model.position;
      }
    }
    return null;
  };

  THIS_SERVICE.animateChairsSet = function (set, direction) {
    if (set) {
      var opval = direction !== true ? 0.1 : 1;
      set.animate({
        opacity: opval
      }, 1000, direction ? mina.easeout : mina.easeoin, function () {
        THIS_SERVICE.animateChairsSet(set, !direction);
      });
    }
  };

  /**
   * Checks if current time is within a service's duration
   */
  THIS_SERVICE.isServiceWithinTime = function (service, time) {
    const durationInMins = moment.duration(moment(time).diff(moment(service.startTime))).asMinutes();
    const serviceDuration = moment.duration(moment(service.endTime).diff(moment(service.startTime))).asMinutes();

    return durationInMins >= 0 && durationInMins < serviceDuration;
  };

  /**
   * Checks if current time is within the period before a service starts, using the service's
   * `bookingDuration` property as a time measurement.
   */
  THIS_SERVICE.isServiceApproaching = function (service, time) {
    const durationInMins = moment.duration(moment(service.startTime).diff(moment(time))).asMinutes();
    const bookingDuration = service.bookingDuration || PlanConst.Bookings.duration;

    /**
     * TODO: this is not getting called when chosing floorplan first, then changing dates.
     */
    return durationInMins >= 0 && durationInMins < bookingDuration;
  };

  /**
   * Checks if a service is a private function event
   */
  THIS_SERVICE.isPrivateFunEvent = function (service) {
    return service && service.type === 'event' && service.eventType === 'PrivateFunction';
  };

  /**
   * Checks if service is a private function and within the time given.
   * Combined some logic here as it gets used in multiple places that should be tightly coupled.
   */
  THIS_SERVICE.isRunningPrivateFun = function (service, time) {
    return THIS_SERVICE.isPrivateFunEvent(service) && THIS_SERVICE.isServiceWithinTime(service, time);
  };

  /**
   * Checks if service is a private function and time is within the period before a service starts.
   * Combined some logic here as it gets used in multiple places that should be tightly coupled.
   */
  THIS_SERVICE.isApproachingPrivateFun = function (service, time) {
    return THIS_SERVICE.isPrivateFunEvent(service) && time && THIS_SERVICE.isServiceApproaching(service, time);
  };


  /**
   * Return this service
   */
  return THIS_SERVICE;
}
